import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SettingsPageShell from "../../components/shared/SettingsPageShell";
import { FONT_MONO } from "../../theme/v2Tokens";
import { COLUMN_DEFS, DEFAULT_LABEL_MAPPING } from "../search/columnDefs";
import { useV2HeaderNames, useV2SaveHeaderNames } from "../../hooks/useV2HeaderNames";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ConfigureHeaderNames() {
  const { t } = useTranslation(["configureHeaders", "common"]);
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { headerNamesMap, isLoading } = useV2HeaderNames(customerId);
  const { save, isPending } = useV2SaveHeaderNames(customerId ?? "");

  // Local form state: field -> label string
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Initialize form values once data loads
  useEffect(() => {
    if (initialized || isLoading) return;
    const values: Record<string, string> = {};
    for (const col of COLUMN_DEFS) {
      const custom = headerNamesMap?.get(col.field);
      values[col.field] = custom?.name ?? DEFAULT_LABEL_MAPPING[col.field] ?? col.field;
    }
    setFormValues(values);
    setInitialized(true);
  }, [headerNamesMap, isLoading, initialized]);

  const handleChange = useCallback((field: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setSaveMessage(null);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const labels: Record<string, { name: string }> = {};
      for (const [field, value] of Object.entries(formValues)) {
        labels[field] = { name: value };
      }
      await save(labels);
      setSaveMessage({ text: t("configureHeaders:headerNamesSavedSuccessfully"), type: "success" });
    } catch (err) {
      console.error("Error saving header names:", err);
      setSaveMessage({ text: t("configureHeaders:failedToSaveHeaderNames"), type: "error" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, save]);

  const handleReset = useCallback(() => {
    const values: Record<string, string> = {};
    for (const col of COLUMN_DEFS) {
      values[col.field] = DEFAULT_LABEL_MAPPING[col.field] ?? col.field;
    }
    setFormValues(values);
    setSaveMessage(null);
  }, []);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <SettingsPageShell
      isLoading={isLoading || !initialized}
      loadingMessage={t("configureHeaders:loadingHeaderNames")}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: "1px solid var(--border-subtle)",
          backgroundColor: "var(--bg-surface)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "var(--text-primary)",
            mb: 0.5,
          }}
        >
          {t("configureHeaders:configureTableHeaders")}
        </Box>
        <Box
          sx={{
            fontSize: "0.8125rem",
            color: "var(--text-tertiary)",
          }}
        >
          {t("configureHeaders:subtitle")}
        </Box>
      </Box>

      {/* Scrollable form */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            maxWidth: 800,
          }}
        >
          {COLUMN_DEFS.map((col) => (
            <Box
              key={col.field}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Box
                component="label"
                htmlFor={`header-${col.field}`}
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {col.field}
              </Box>
              <Box
                component="input"
                id={`header-${col.field}`}
                type="text"
                value={formValues[col.field] ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(col.field, e.target.value)
                }
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.8125rem",
                  px: 1.5,
                  py: 1,
                  borderRadius: "6px",
                  border: "1px solid var(--border-mid)",
                  backgroundColor: "var(--bg-deep)",
                  color: "var(--text-primary)",
                  outline: "none",
                  transition: "border-color 150ms ease",
                  "&:focus": {
                    borderColor: "var(--accent)",
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Footer with actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid var(--border-subtle)",
          backgroundColor: "var(--bg-surface)",
          flexShrink: 0,
        }}
      >
        <Box
          component="button"
          onClick={handleSave}
          disabled={isPending}
          sx={{
            px: 2.5,
            py: 1,
            borderRadius: "6px",
            border: "none",
            backgroundColor: "var(--accent)",
            color: "#fff",
            fontSize: "0.8125rem",
            fontWeight: 600,
            cursor: isPending ? "not-allowed" : "pointer",
            opacity: isPending ? 0.6 : 1,
            fontFamily: "inherit",
            "&:hover": {
              opacity: 0.9,
            },
          }}
        >
          {isPending ? t("common:saving") : t("common:save")}
        </Box>
        <Box
          component="button"
          onClick={handleReset}
          sx={{
            px: 2.5,
            py: 1,
            borderRadius: "6px",
            border: "1px solid var(--border-mid)",
            backgroundColor: "transparent",
            color: "var(--text-secondary)",
            fontSize: "0.8125rem",
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            "&:hover": {
              backgroundColor: "var(--bg-hover)",
            },
          }}
        >
          {t("configureHeaders:resetToDefault")}
        </Box>
        <Box
          component="button"
          onClick={handleCancel}
          sx={{
            px: 2.5,
            py: 1,
            borderRadius: "6px",
            border: "1px solid var(--border-mid)",
            backgroundColor: "transparent",
            color: "var(--text-secondary)",
            fontSize: "0.8125rem",
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            "&:hover": {
              backgroundColor: "var(--bg-hover)",
            },
          }}
        >
          {t("common:cancel")}
        </Box>

        {/* Save feedback */}
        {saveMessage && (
          <Box
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 500,
              color:
                saveMessage.type === "success"
                  ? "var(--accent)"
                  : "var(--signal-rose)",
              ml: 1,
            }}
          >
            {saveMessage.text}
          </Box>
        )}
      </Box>
    </SettingsPageShell>
  );
}
